import { CommonModule } from '@angular/common';
import { Component, OnInit, Input, OnChanges, SimpleChanges, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { TRANSLOCO_SCOPE, TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { TranslationService } from 'src/app/services/translation.service';
import { QaiTableComponent, TableRow, QaiLoadingComponent, HeaderConfig, SortDirection } from '@qai/ui';
import { IPracticeSimulation } from 'src/app/interfaces/reportingV2/ipractice-simulation';
import { ReportingDashboardService } from 'src/app/services/reporting-dashboard/reporting-dashboard.service';
import { Subscription } from 'rxjs';
import { LocaleService } from 'src/app/services/locale.service';
import { AmplitudeService } from 'src/app/services/external-tools/amplitude.service';
import { AmplitudeTrackingEvents } from 'src/app/utils/enums/amplitude/amplitudeTrackingEvents.enum';
import { withDemoModule } from 'src/app/utils/demo-mode/demo-mode-context.util';
import { AppConstants } from 'src/app/app-constants';

@Component({
  selector: 'app-practice-simulations',
  templateUrl: './practice-simulations.component.html',
  styleUrls: ['./practice-simulations.component.scss'],
  standalone: true,
  providers: [
    {
      provide: TRANSLOCO_SCOPE,
      useValue: 'reportingV2',
    }
  ],
  imports: [TranslocoModule, CommonModule, QaiTableComponent, QaiLoadingComponent]
})
export class PracticeSimulationsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() assignmentId: string | null = null;
  @Input() selectedTeamIds: string[] = [];
  @Input() selectedAccountUuid!: string | null;
  @Input() assignmentName: string = '';
  @Input() engagementStatus: string | null = null;

  isTranslationLoaded: boolean = false;
  isLoading: boolean = false;
  tableData: TableRow[] = [];
  headers: HeaderConfig[] = [];
  initialSortColumn: string = '';
  initialSortDirection: SortDirection = 'asc';
  currentPage: number = 1;
  pageSize: number = 3;
  private practiceSimulationSub?: Subscription;

  practiceSimulations: IPracticeSimulation[] = [];

  constructor(
    private translocoService: TranslocoService,
    private translationService: TranslationService,
    private reportingDashboardService: ReportingDashboardService,
    private localeService: LocaleService,
    private router: Router,
    private amplitudeService: AmplitudeService
  ) { }

  ngOnInit() {
    this.translationService.loadScopedTranslation('reportingV2').then(() => {
      this.isTranslationLoaded = true;
      this.initializeTable();
      this.loadSimulations();
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    if ((changes['assignmentId'] && !changes['assignmentId'].firstChange) || (changes['selectedTeamIds'] && !changes['selectedTeamIds'].firstChange)) 
    {
      this.loadSimulations();
    }
  }

  private initializeTable() {
    this.headers = [
      {
        name: this.translocoService.translate('SIMULATION_NAME', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '28%'
      },
      {
        name: this.translocoService.translate('AVERAGE_DURATION_OF_SESSION', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '20%'
      },
      {
        name: this.translocoService.translate('AVERAGE_OVERALL_SCORE', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '17%'
      },
      {
        name: this.translocoService.translate('TOTAL_SESSIONS_LABEL', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '16%'
      },
      {
        name: this.translocoService.translate('AVERAGE_SESSIONS_PER_ACTIVE_USER', {}, 'reportingV2'),
        sortable: true,
        columnWidth: '19%'
      }
    ];

    this.initialSortColumn = this.translocoService.translate('SIMULATION_NAME', {}, 'reportingV2');
    this.initialSortDirection = 'asc';
  }

  private loadSimulations() {
    if (this.practiceSimulationSub) {
      this.practiceSimulationSub.unsubscribe();
    }

    if (!this.assignmentId || !this.selectedAccountUuid) {
        this.isLoading = false;
        return;
    }

    this.isLoading = true;
  
    const moduleAssignmentUuid = this.assignmentId || '';

    this.reportingDashboardService
      .getPracticeSimulationMetrics(
        this.selectedAccountUuid,
        moduleAssignmentUuid,
        this.selectedTeamIds.includes('all') ? [] : this.selectedTeamIds,
        withDemoModule(AppConstants.NEW_REPORTING_UI)
      )
      .subscribe({
        next: (simulations: IPracticeSimulation[]) => {
          this.practiceSimulations = simulations;
          this.tableData = this.createTableRows(simulations);
          this.currentPage = 1;
          this.isLoading = false;
        },
        error: (error) => {
          console.error('Failed to load simulation metrics:', error);
          this.isLoading = false;
        },
      });
  }

  private createTableRows(simulations: IPracticeSimulation[]): TableRow[] {
    const safeValue = (val: any, formatter?: (v: any) => string): string => {
      if (val === null || val === undefined) {
        return '—';
      }
      return formatter ? formatter(val) : val.toString();
    };

    return simulations.map(simulation => {
      const row: any = {
        id: simulation.uuid,
        [this.translocoService.translate('SIMULATION_NAME', {}, 'reportingV2')]: {
          type: 'text',
          value: safeValue(simulation.simulationName)
        },
        [this.translocoService.translate('AVERAGE_DURATION_OF_SESSION', {}, 'reportingV2')]: {
          type: 'text',
          value: safeValue(simulation.avgDurationSeconds, v => this.formatDuration(v))
        },
        [this.translocoService.translate('AVERAGE_OVERALL_SCORE', {}, 'reportingV2')]: {
          type: 'text',
          value: safeValue(simulation.avgOverallScore, v => this.localeService.formatNumber(v,1))
        },
        [this.translocoService.translate('TOTAL_SESSIONS_LABEL', {}, 'reportingV2')]: {
          type: 'text',
          value: safeValue(simulation.totalSessions, v => this.localeService.formatNumber(v))
        },
        [this.translocoService.translate('AVERAGE_SESSIONS_PER_ACTIVE_USER', {}, 'reportingV2')]: {
          type: 'text',
          value: safeValue(simulation.avgSessionsPerActiveUser, v => this.localeService.formatNumber(v,1))
        }
      };

      return row as TableRow;
    });
  }

  private formatDuration(seconds: number | null | undefined): string {
    if (seconds == null || isNaN(seconds)) return '—';

    const totalSeconds = Math.floor(seconds);
    const minutes = Math.floor(totalSeconds / 60);
    const remainingSeconds = totalSeconds % 60;

    const minLabel = this.translocoService.translate(
      minutes === 1 ? 'MINUTE' : 'MINUTES',
      {},
      'reportingV2'
    );
    const secLabel = this.translocoService.translate(
      remainingSeconds === 1 ? 'SECOND' : 'SECONDS',
      {},
      'reportingV2'
    );

    const formattedMinutes = this.localeService.formatNumber(minutes);
    const formattedSeconds = this.localeService.formatNumber(remainingSeconds);

    if (minutes > 0 && remainingSeconds > 0) {
      return `${formattedMinutes} ${minLabel} ${formattedSeconds} ${secLabel}`;
    } else if (minutes > 0) {
      return `${formattedMinutes} ${minLabel}`;
    } else {
      return `${formattedSeconds} ${secLabel}`;
    }
  }

  onTableRowClick(event: { rowId: string | number; rowData: TableRow }): void {
    const simulation = this.practiceSimulations.find(sim => sim.uuid === event.rowId);
    
    if (simulation) {
      this.amplitudeService.trackEvent(
        AmplitudeTrackingEvents.ASSIGNMENTREPORT_CLICK_SIMULATIONTABLE_SIMNAME,
        { simulation_name: simulation.simulationName }
      );
      
      this.router.navigate(['reporting_dashboard/simulation', event.rowId], {
        queryParams: {
          moduleAssignmentUuid: this.assignmentId
        },
        state: {
          assignmentName: this.assignmentName,
          simulationName: simulation.simulationName,
          selectedTeamUuids: this.selectedTeamIds ?? [],
          engagementStatus: this.engagementStatus,
        }
      });
    }
  }

  ngOnDestroy(): void {
    this.practiceSimulationSub?.unsubscribe();
  }

}



